// PostVictorySummary — detailed match stats screen shown after victory.
// Derives all stats from existing snapshot data (no Rust changes needed).
// The card paints its character art under the theme's `--panel-scrim`, so it
// follows the app theme (cream over the art in light mode, ink in dark).

import type { GameSnapshot } from '@aod/protocol';
import { Button } from '@aod/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@aod/ui/components/ui/card';
import { Separator } from '@aod/ui/components/ui/separator';
import {
  BookOpen,
  Building2,
  Coins,
  Crown,
  ScrollText,
  Swords,
  Timer,
  TrendingUp,
} from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { type MessageKey, useT } from '../i18n/index.js';
import { cityOwnerId } from '../ownership.js';
import { formatGameTime } from '../sim/gameTime.js';

interface PostVictorySummaryProps {
  readonly snapshot: GameSnapshot;
  readonly winner: number;
  readonly path: string;
  readonly localPlayerIndex: number;
  readonly onNewMatch: () => void;
  readonly onReturnToMenu?: () => void;
}

interface PlayerStats {
  playerIndex: number;
  cities: number;
  units: number;
  gold: number;
  industry: number;
  knowledge: number;
  faith: number;
  food: number;
  doctrinesSpent: number;
  isWinner: boolean;
  isLocal: boolean;
}

export function PostVictorySummary({
  snapshot,
  winner,
  path,
  localPlayerIndex,
  onNewMatch,
  onReturnToMenu,
}: PostVictorySummaryProps) {
  const t = useT();
  const pathKey = `victory.path.${path}`;
  const pathLabel = VICTORY_PATHS.has(path) ? t(pathKey as MessageKey) : path;
  const stats = useMemo(
    () => extractStats(snapshot, winner, localPlayerIndex),
    [snapshot, winner, localPlayerIndex],
  );
  const tick = snapshot.frame;
  const winnerStats = stats.find((s) => s.isWinner);

  return (
    <div className="pointer-events-none fixed inset-0 z-[55] flex items-center justify-center">
      <Card
        className="pointer-events-auto w-[min(92vw,520px)] max-h-[85vh] overflow-y-auto border-amber-500/50 text-card-foreground shadow-2xl backdrop-blur dark:border-amber-400/60"
        style={{
          background: `linear-gradient(var(--panel-scrim), var(--panel-scrim)), url('/assets/images/ui/character_bg.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Crown className="size-5 text-amber-600 dark:text-amber-400" />
            {t('summary.title')}
          </CardTitle>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Timer className="size-3.5" />
              {formatGameTime(BigInt(tick))}
            </span>
            <span className="flex items-center gap-1">
              <Crown className="size-3.5 text-amber-600 dark:text-amber-400" />
              {t('common.playerWins', { player: (winnerStats?.playerIndex ?? winner) + 1 })}
              {' — '}
              {pathLabel}
            </span>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Per-player stats */}
          {stats.map((ps) => (
            <div
              key={ps.playerIndex}
              className={`rounded-lg border p-4 ${ps.isWinner ? 'border-amber-500/40 bg-amber-500/10 dark:border-amber-400/40 dark:bg-amber-400/5' : 'border-border bg-muted/30'}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="size-3 rounded-full"
                  style={{
                    background: ps.isWinner
                      ? '#e6b422'
                      : ps.playerIndex === 0
                        ? '#3b82f6'
                        : '#f97316',
                  }}
                />
                <span className="font-semibold text-sm">
                  {t('common.player', { player: ps.playerIndex + 1 })}
                  {ps.isLocal && (
                    <span className="text-muted-foreground ml-1">{t('summary.you')}</span>
                  )}
                </span>
                {ps.isWinner && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 font-medium text-amber-700 dark:bg-amber-400/15 dark:text-amber-300">
                    {t('summary.winner')}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <Stat
                  icon={<Building2 className="size-3" />}
                  label={t('summary.cities')}
                  value={ps.cities}
                />
                <Stat
                  icon={<Swords className="size-3" />}
                  label={t('summary.units')}
                  value={ps.units}
                />
                <Stat
                  icon={<ScrollText className="size-3" />}
                  label={t('summary.doctrines')}
                  value={ps.doctrinesSpent}
                />
                <Stat
                  icon={<Coins className="size-3" />}
                  label={t('summary.gold')}
                  value={ps.gold}
                />
                <Stat
                  icon={<TrendingUp className="size-3" />}
                  label={t('summary.industry')}
                  value={ps.industry}
                />
                <Stat
                  icon={<BookOpen className="size-3" />}
                  label={t('summary.knowledge')}
                  value={ps.knowledge}
                />
              </div>
            </div>
          ))}

          <Separator />

          {/* Actions */}
          <div className="flex gap-2">
            {onReturnToMenu && (
              <Button type="button" variant="outline" className="flex-1" onClick={onReturnToMenu}>
                {t('summary.returnToMenu')}
              </Button>
            )}
            <Button type="button" className="flex-1" onClick={onNewMatch}>
              {t('summary.playAgain')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const VICTORY_PATHS = new Set(['conquest', 'economy', 'science', 'faith', 'diplomacy']);

function Stat({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5 text-muted-foreground">
      {icon}
      <span className="text-[10px]">{label}</span>
      <span className="font-medium text-foreground tabular-nums ml-auto">{value}</span>
    </div>
  );
}

function extractStats(
  snapshot: GameSnapshot,
  winner: number,
  localPlayerIndex: number,
): PlayerStats[] {
  const children = snapshot.scene.children ?? [];
  const globals = snapshot.state.globals as Record<string, unknown> | undefined;
  const players = (globals?.players as Array<Record<string, unknown>>) ?? [];

  const cityCounts = new Map<number, number>();
  const unitCounts = new Map<number, number>();

  for (const node of children) {
    const ownerId =
      node.type === 'city' ? cityOwnerId(node.props) : (node.props?.ownerId as number | undefined);
    if (ownerId === undefined) continue;
    if (node.type === 'city') {
      cityCounts.set(ownerId, (cityCounts.get(ownerId) ?? 0) + 1);
    } else if (node.type === 'unit') {
      unitCounts.set(ownerId, (unitCounts.get(ownerId) ?? 0) + 1);
    }
  }

  return players.map((p) => {
    const pi = p.playerIndex as number;
    const treasury = p.treasury as Record<string, number> | undefined;
    const doctrine = p.doctrine as { spent: number[] } | undefined;
    return {
      playerIndex: pi,
      cities: cityCounts.get(pi) ?? 0,
      units: unitCounts.get(pi) ?? 0,
      gold: treasury?.gold ?? 0,
      industry: treasury?.industry ?? 0,
      knowledge: treasury?.knowledge ?? 0,
      faith: treasury?.faith ?? 0,
      food: treasury?.food ?? 0,
      doctrinesSpent: doctrine?.spent?.length ?? 0,
      isWinner: pi === winner,
      isLocal: pi === localPlayerIndex,
    };
  });
}
